import type {
  ApiVersion,
  AuthMeResponse,
  AuthOtpRequestResponse,
  AuthOtpVerifyResponse,
  OnboardingCompleteBody,
  OnboardingCompleteResponse,
  UserMeResponse,
  UserPatchBody,
} from '@butler/shared';

// C3 — LLM 일 한도 사용량 응답
export interface UsageTodayResponse {
  usage: {
    day: string;
    callCount: number;
    inputTokens: number;
    outputTokens: number;
    costKrw: number;
  };
  limit: { callLimit: number; krwLimit: number };
  remaining: { calls: number; krw: number };
}

const API_BASE: string = (import.meta as ImportMeta).env?.VITE_API_BASE ?? 'http://localhost:3001';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = window.Butler?.getAuthToken?.() ?? null;
  const headers = new Headers(init.headers ?? {});
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, (body as { error?: string })?.error ?? `http-${res.status}`, body);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, code: string, body: unknown) {
    super(`${status}: ${code}`);
    this.status = status;
    this.body = body;
  }
}

// =====================================================================================
// API 응답 타입 — apps/api/src/routes/*.ts와 1:1 매칭 (날짜는 string으로 직렬화)
// =====================================================================================

export interface CallListItem {
  id: string;
  started_at: string;
  duration_seconds: number | null;
  contact_phone: string | null;
  contact_name: string | null;
  direction: 'in' | 'out' | 'missed' | null;
  category: 'work' | 'service' | 'personal' | 'etc' | null;
  status: 'captured' | 'uploaded' | 'transcribing' | 'summarizing' | 'ready' | 'failed';
}
export interface CallListResponse {
  items: CallListItem[];
  next_cursor: string | null;
}

export interface SpeakerSegment {
  start: number;
  end: number;
  speaker: string;
  text: string;
}
export interface CallDetail {
  call: {
    id: string;
    started_at: string;
    duration_seconds: number | null;
    contact_phone: string | null;
    contact_name: string | null;
    direction: 'in' | 'out' | 'missed' | null;
    category: 'work' | 'service' | 'personal' | 'etc' | null;
    status: 'captured' | 'uploaded' | 'transcribing' | 'summarizing' | 'ready' | 'failed';
    error: string | null;
  };
  transcript: {
    id: string;
    fullText: string;
    speakerSegments: SpeakerSegment[];
    provider: string;
  } | null;
  summary: {
    id: string;
    topic: string | null;
    bullets: string[] | null;
    followups: string[] | null;
    model: string;
  } | null;
}

export interface SmsListItem {
  id: string;
  received_at: string;
  from_phone: string | null;
  body: string;
  direction: 'in' | 'out';
}
export interface SmsListResponse {
  items: SmsListItem[];
  next_cursor: string | null;
}

export interface TodoItem {
  id: string;
  content: string;
  status: 'new' | 'in_progress' | 'done' | 'dismissed';
  dueAt: string | null;
  sourceType: 'call' | 'sms';
  sourceId: string;
  confidence: number | null;
  createdAt: string;
}
export interface TodoListResponse {
  items: TodoItem[];
}

// P9 — Suggestion / Calendar -----------------------------------------------------------
export interface SuggestionPayload {
  kind: 'calendar_event';
  title: string;
  start_time: string;
  end_time: string;
  location: string | null;
  description: string | null;
  attendees: Array<{ email?: string; name?: string }>;
}
export interface ActionSuggestion {
  id: string;
  userId: string;
  kind: string;
  sourceType: 'call' | 'sms';
  sourceId: string;
  payload: SuggestionPayload;
  sourceQuote: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'modified';
  resolvedExternalId: string | null;
  createdAt: string;
  resolvedAt: string | null;
}
export interface SuggestionListResponse {
  items: ActionSuggestion[];
}
export interface CalendarCredential {
  id: string;
  provider: 'google' | 'naver';
  account_email: string | null;
  created_at: string;
  revoked_at: string | null;
}
export interface CalendarCredentialsResponse {
  items: CalendarCredential[];
}

// P7 — Chat ----------------------------------------------------------------------------
export interface ChatThread {
  id: string;
  title: string | null;
  createdAt: string;
  lastMessageAt: string;
}
export interface ChatMessage {
  id: string;
  threadId: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  contentText: string;
  retrievedSources: Array<{ type: string; id: string; label: string }> | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costKrw: number | null;
  createdAt: string;
}
export interface ChatSendResponse {
  thread_id: string;
  user_message: ChatMessage;
  assistant_message: ChatMessage;
  sources: Array<{ type: string; id: string; label: string }>;
  meta: { callCount: number; smsCount: number; todoCount: number; eventCount: number; cost_krw?: number; mocked?: boolean };
}

export const api = {
  otpRequest: (phone: string) =>
    request<AuthOtpRequestResponse>('/api/auth/otp/request', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    }),
  otpVerify: (phone: string, code: string, device_label?: string) =>
    request<AuthOtpVerifyResponse>('/api/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ phone, code, device_label }),
    }),
  me: () => request<AuthMeResponse>('/api/auth/me'),
  logout: () => request<{ ok: boolean }>('/api/auth/session', { method: 'DELETE' }),

  // H3 — APK 부팅 시 버전 체크 (Bearer 불필요)
  getVersion: () => request<ApiVersion>('/api/version'),

  // C3 — LLM 일 한도 사용량
  usageToday: () => request<UsageTodayResponse>('/api/usage/today'),

  // H5 — 회원 탈퇴 (soft delete)
  deleteAccount: () =>
    request<{ ok: boolean; deleted_at: string; already_deleted?: boolean; hint?: string }>(
      '/api/users/me',
      { method: 'DELETE', body: JSON.stringify({ confirm: 'DELETE_MY_ACCOUNT' }) },
    ),

  // P11 — 사용자 정보 + AI 온보딩 -----------------------------------------------------
  usersMe: () => request<UserMeResponse>('/api/users/me'),
  patchUsersMe: (body: UserPatchBody) =>
    request<UserMeResponse>('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // onboardingTurn(/api/onboarding/turn, Sonnet+web_search) 제거 — 온보딩 방식 A(LLM 0회)로 전환.
  onboardingComplete: (body: OnboardingCompleteBody) =>
    request<OnboardingCompleteResponse>('/api/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // P3 데이터 조회 ---------------------------------------------------------------------
  // 통화(calls)는 폰 Room으로 이전됨 — lib/roomData.ts(listCallsFromRoom/getCallFromRoom) 사용.
  // 서버 /api/calls 라우트는 P1에서 삭제됨. (sms/todos 등은 해당 페이즈에서 이전 예정)
  listSms: (limit = 100) => request<SmsListResponse>(`/api/sms?limit=${limit}`),
  listTodos: (statuses: Array<'new' | 'in_progress' | 'done' | 'dismissed'> = ['new', 'in_progress', 'done']) =>
    request<TodoListResponse>(`/api/todos?status=${statuses.join(',')}`),
  patchTodo: (id: string, body: { content?: string; status?: 'new' | 'in_progress' | 'done' | 'dismissed'; due_at?: string | null }) =>
    request<{ item: TodoItem }>(`/api/todos/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  dismissTodo: (id: string) =>
    request<{ ok: boolean }>(`/api/todos/${id}`, { method: 'DELETE' }),

  // P7 — Chat -------------------------------------------------------------------------
  listChatThreads: () => request<{ items: ChatThread[] }>('/api/chat/threads'),
  getChatMessages: (threadId: string) =>
    request<{ items: ChatMessage[] }>(`/api/chat/threads/${threadId}/messages`),
  sendChatMessage: (threadId: string | 'new', content: string) =>
    request<ChatSendResponse>(`/api/chat/threads/${threadId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
  deleteChatThread: (threadId: string) =>
    request<{ ok: boolean }>(`/api/chat/threads/${threadId}`, { method: 'DELETE' }),

  // P9 — Calendar + Suggestions -------------------------------------------------------
  listSuggestions: (status: 'pending' | 'approved' | 'rejected' | 'modified' = 'pending') =>
    request<SuggestionListResponse>(`/api/suggestions?status=${status}`),
  approveSuggestion: (id: string, payload?: Partial<SuggestionPayload>) =>
    request<{ ok: boolean; external_id: string; html_link: string | null }>(
      `/api/suggestions/${id}/approve`,
      { method: 'POST', body: JSON.stringify(payload ? { payload } : {}) },
    ),
  rejectSuggestion: (id: string) =>
    request<{ ok: boolean }>(`/api/suggestions/${id}/reject`, { method: 'POST' }),
  listCalendarCredentials: () =>
    request<CalendarCredentialsResponse>('/api/calendar/credentials'),
  startCalendarOAuth: (provider: 'google' | 'naver') =>
    request<{ authorize_url: string }>(`/api/calendar/oauth/start?provider=${provider}`),
  revokeCalendarCredential: (id: string) =>
    request<{ ok: boolean }>(`/api/calendar/credentials/${id}`, { method: 'DELETE' }),
  getRecordingFolders: () =>
    request<{ folders: RecordingFolder[] }>('/api/users/me/recording-folders'),
  setRecordingFolders: (folders: RecordingFolder[]) =>
    request<{ folders: RecordingFolder[] }>(
      '/api/users/me/recording-folders',
      { method: 'PUT', body: JSON.stringify({ folders }) },
    ),
};

export interface RecordingFolder {
  path: string;
  source: 'auto' | 'saf' | 'manual';
  enabled: boolean;
  discovered_at?: string;
}

export interface ScannedFolderCandidate {
  path: string;
  sample_count: number;
  latest_at: number;       // epoch seconds
  confidence: 'high' | 'medium' | 'low';
}
